import { useEffect, useRef, useState, useCallback } from 'react';
import { Plugin } from './types';

export interface PlaygroundConfig {
  enabled: boolean;
  wsUrl?: string;
  port?: number;
  reconnectInterval?: number;
  debug?: boolean;
  showDebugPanel?: boolean;
  showMinimalIndicator?: boolean;
  debugPanelPosition?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
}

export interface PlaygroundState {
  isConnected: boolean;
  isConnecting: boolean;
  error: string | null;
  lastUpdate: number | null;
  receivedPlugins: string[];
}

const DEFAULT_CONFIG: Required<PlaygroundConfig> = {
  enabled: false,
  wsUrl: 'ws://localhost:11400',
  port: 11400,
  reconnectInterval: 3000,
  debug: false,
  showDebugPanel: false,
  showMinimalIndicator: true,
  debugPanelPosition: 'bottom-right'
};

export const usePlaygroundClient = (
  config: PlaygroundConfig,
  onPluginUpdate?: (plugin: Plugin) => void,
  onCodeUpdate?: (pluginName: string, code: string) => void,
  onPluginError?: (pluginName: string, error: string) => void
) => {
  const [state, setState] = useState<PlaygroundState>({
    isConnected: false,
    isConnecting: false,
    error: null,
    lastUpdate: null,
    receivedPlugins: []
  });

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const configRef = useRef<Required<PlaygroundConfig>>(DEFAULT_CONFIG);

  // Update config ref when config changes
  useEffect(() => {
    configRef.current = { ...DEFAULT_CONFIG, ...config };
  }, [config]);

  const log = useCallback((message: string, ...args: any[]) => {
    if (configRef.current.debug) {
      console.log(`[Playground] ${message}`, ...args);
    }
  }, []);

  const evaluatePluginCode = useCallback((pluginName: string, code: string): Plugin | null => {
    try {
      // Create a sandboxed environment for plugin execution
      const pluginExports = {};
      const pluginModule = { exports: pluginExports };
      
      // Create a function to execute the plugin code
      const executePlugin = new Function(
        'module', 
        'exports', 
        'React', 
        'require',
        `
        // Create a mock require function for common dependencies
        const mockRequire = (id) => {
          if (id === 'react') return React;
          if (id === 'react-dom') return { render: () => {} };
          throw new Error('Module not found: ' + id + '. Use external dependencies.');
        };
        
        ${code}
        
        return module.exports;
        `
      );

      // Execute the plugin code
      const reactObject = window.React || require('react');
      
      const pluginResult = executePlugin(
        pluginModule, 
        pluginExports, 
        reactObject,
        () => { throw new Error('require is not available in playground mode'); }
      );

      // Handle different export patterns
      let plugin: Plugin;
      
      if (pluginResult && typeof pluginResult === 'object') {
        // ES6 default export or direct plugin object
        plugin = pluginResult.default || pluginResult;
      } else if (typeof pluginResult === 'function') {
        // Function that returns a plugin
        plugin = pluginResult();
      } else {
        throw new Error('Plugin must export a valid plugin object');
      }

      // Validate plugin structure
      if (!plugin || !plugin.meta || !plugin.meta.name) {
        throw new Error('Plugin must have a meta.name property');
      }

      // Ensure plugin name matches expected name
      if (plugin.meta.name !== pluginName) {
        log(`Plugin name mismatch: expected ${pluginName}, got ${plugin.meta.name}`);
        plugin.meta.name = pluginName; // Override with expected name
      }

      // Add playground metadata
      plugin.meta = {
        ...plugin.meta,
        isPlayground: true,
        loadedAt: new Date().toISOString(),
        source: 'playground'
      };

      log('Successfully evaluated plugin:', pluginName);
      return plugin;

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      log('Error evaluating plugin code:', errorMessage);
      onPluginError?.(pluginName, errorMessage);
      return null;
    }
  }, [log, onPluginError]);

  const handleMessage = useCallback((event: MessageEvent) => {
    try {
      const message = JSON.parse(event.data);
      log('Received message:', message.type, message);

      switch (message.type) {
        case 'welcome':
          log('Connected to playground server');
          break;

        case 'plugin-update':
          const { pluginName, code, timestamp } = message;
          
          if (!pluginName || !code) {
            log('Invalid plugin update message', message);
            return;
          }

          setState(prev => ({
            ...prev,
            lastUpdate: timestamp || Date.now(),
            receivedPlugins: Array.from(new Set([...prev.receivedPlugins, pluginName]))
          }));
          
          // Send raw code to be processed like Thunder plugins
          if (onCodeUpdate) {
            onCodeUpdate(pluginName, code);
            log('Plugin code sent for processing:', pluginName);
          } else {
            // Fallback to old behavior for compatibility
            const plugin = evaluatePluginCode(pluginName, code);
            if (plugin) {
              onPluginUpdate?.(plugin);
              log('Plugin updated successfully:', pluginName);
            }
          }
          break;

        case 'plugin-error':
          const { pluginName: errorPluginName, error } = message;
          log('Plugin error received:', errorPluginName, error);
          onPluginError?.(errorPluginName, error);
          break;

        case 'pong':
          log('Received pong');
          break;

        default:
          log('Unknown message type:', message.type);
      }
    } catch (error) {
      log('Error parsing message:', error);
    }
  }, [log, evaluatePluginCode, onPluginUpdate, onPluginError]);

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      log('Already connected');
      return;
    }

    setState(prev => ({ ...prev, isConnecting: true, error: null }));
    
    const wsUrl = configRef.current.wsUrl || `ws://localhost:${configRef.current.port}`;
    log('Connecting to:', wsUrl);
    
    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        log('WebSocket connected');
        setState(prev => ({ 
          ...prev, 
          isConnected: true, 
          isConnecting: false, 
          error: null 
        }));

        // Request current plugins
        ws.send(JSON.stringify({ type: 'request-plugins' }));
      };

      ws.onmessage = handleMessage;

      ws.onclose = (event) => {
        const codeMessages = {
          1000: 'Normal closure',
          1001: 'Going away',
          1002: 'Protocol error',
          1003: 'Unsupported data',
          1004: 'Reserved',
          1005: 'No status received',
          1006: 'Abnormal closure',
          1007: 'Invalid frame payload data',
          1008: 'Policy violation',
          1009: 'Message too big',
          1010: 'Missing extension',
          1011: 'Internal error',
          1015: 'TLS handshake failure'
        };
        
        const codeMessage = codeMessages[event.code as keyof typeof codeMessages] || 'Unknown';
        const errorMessage = event.reason || `Connection closed: ${codeMessage} (${event.code})`;
        
        log('WebSocket closed', event.code, event.reason, '- Reason:', codeMessage);
        setState(prev => ({ 
          ...prev, 
          isConnected: false, 
          isConnecting: false,
          error: errorMessage
        }));

        // Attempt to reconnect if enabled
        if (configRef.current.enabled && configRef.current.reconnectInterval > 0) {
          log(`Attempting to reconnect in ${configRef.current.reconnectInterval}ms...`);
          reconnectTimeoutRef.current = setTimeout(() => {
            log('Attempting to reconnect...');
            connect();
          }, configRef.current.reconnectInterval);
        }
      };

      ws.onerror = (error) => {
        log('WebSocket error:', error);
        setState(prev => ({ 
          ...prev, 
          isConnected: false, 
          isConnecting: false,
          error: 'Connection error'
        }));
      };

    } catch (error) {
      log('Failed to create WebSocket:', error);
      setState(prev => ({ 
        ...prev, 
        isConnecting: false, 
        error: error instanceof Error ? error.message : 'Connection failed'
      }));
    }
  }, [handleMessage, log]);

  const disconnect = useCallback(() => {
    log('Disconnecting...');
    
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    setState(prev => ({ 
      ...prev, 
      isConnected: false, 
      isConnecting: false,
      error: null
    }));
  }, [log]);

  const sendPing = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'ping' }));
    }
  }, []);

  // Effect to handle connection based on config
  useEffect(() => {
    if (configRef.current.enabled) {
      connect();
    } else {
      disconnect();
    }

    return () => {
      disconnect();
    };
  }, [config.enabled]); // Only reconnect when enabled state changes

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  return {
    state,
    connect,
    disconnect,
    sendPing,
    isEnabled: configRef.current.enabled
  };
};
